"use client";

import { useState } from "react";

// `added` is a UTC YYYY-MM-DD string; a code is "new" if it was added today.
function isNew(added?: string): boolean {
  return !!added && added === new Date().toISOString().slice(0, 10);
}

export default function CopyCodeButton({ code, reward, added }: { code: string; reward: string; added?: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  return (
    <article
      style={{
        borderRadius: 20,
        border: "1.5px solid #dde1e7",
        boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
      }}
      className="flex flex-col gap-4 bg-white p-5"
    >
      {/* Badge row */}
      <div className="flex items-center gap-2">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold"
          style={{ background: "#eaf7f1", color: "#1a9e5c" }}
        >
          <span className="h-2 w-2 rounded-full" style={{ background: "#1a9e5c" }} />
          Active
        </span>
        {isNew(added) && (
          <span
            className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold"
            style={{ background: "#eff6ff", color: "#1877F2" }}
          >
            New
          </span>
        )}
      </div>

      {/* Big code */}
      <div>
        <div
          style={{
            fontFamily: "var(--font-geist-mono), monospace",
            fontSize: 32,
            fontWeight: 800,
            letterSpacing: "-1px",
            lineHeight: 1,
            color: "#0f172a",
          }}
        >
          {code}
        </div>
        <p className="mt-2 text-sm text-gray-500">{reward}</p>
      </div>

      {/* Copy button */}
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? "Copied!" : `Copy code ${code}`}
        style={{
          borderRadius: 12,
          padding: "11px 20px",
          background: copied ? "#16a34a" : "#1877F2",
          transition: "background 0.2s",
          fontSize: 15,
        }}
        className="flex w-full items-center justify-center gap-2.5 font-bold text-white"
      >
        {copied ? (
          <>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Copied!
          </>
        ) : (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
            Copy Code
          </>
        )}
      </button>
    </article>
  );
}
